import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Search, 
  MapPin, 
  Award, 
  Languages, 
  Calendar,
  Mail,
  FileText,
  X,
  Filter,
  ChevronDown,
  Briefcase
} from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import SEO from "@/components/SEO";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const ProsDirectory = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    availability: "",
    specialty: "",
    language: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [contactModal, setContactModal] = useState(null);
  const [contactMessage, setContactMessage] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [sendingContact, setSendingContact] = useState(false);
  const [selectedPro, setSelectedPro] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        setContactEmail(parsed.email || "");
      } catch (e) {
        setUser(null);
      }
    }
    fetchPros();
  }, []);

  const fetchPros = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let url = `${API}/pros`;
      const params = new URLSearchParams();
      if (filters.availability) params.append("availability", filters.availability);
      if (filters.specialty) params.append("specialty", filters.specialty);
      if (filters.language) params.append("language", filters.language);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, { headers });
      setPros(res.data);
    } catch (error) {
      console.error("Failed to fetch pros:", error);
      toast.error("Failed to load professionals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPros();
  }, [filters]);

  const canViewFullProfile = user?.role === "dive_center" || user?.role === "admin";
  const canContact = user?.role === "dive_center" || user?.role === "admin";

  const handleContact = async () => {
    if (!contactMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSendingContact(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.post(`${API}/contact/pro`, {
        pro_user_id: contactModal.id,
        message: contactMessage,
        sender_email: contactEmail || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message sent successfully!");
      setContactModal(null);
      setContactMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSendingContact(false);
    }
  };

  const filteredPros = pros.filter(pro => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = pro.name?.toLowerCase() || "";
    const profile = pro.pro_profile || {};
    const certs = (profile.certifications || []).map(c => `${c.name} ${c.level}`.toLowerCase()).join(" ");
    const specialties = (profile.specialties || []).join(" ").toLowerCase();
    return name.includes(term) || certs.includes(term) || specialties.includes(term);
  });

  const availabilityOptions = ["available", "looking", "employed"];
  const specialtyOptions = ["Technical Diving", "Cave Diving", "Wreck Diving", "Underwater Photography", "Freediving", "Night Diving", "Deep Diving", "Nitrox"];
  const languageOptions = ["English", "Spanish", "French", "German", "Italian", "Portuguese", "Chinese", "Japanese", "Arabic"];

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white">
        <SEO title="Pros Directory | George Dives" />
        <Navigation />
        <div className="pt-32 pb-16">
          <div className="container mx-auto px-4 text-center">
            <Award className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-4">Dive Professionals Directory</h1>
            <p className="text-gray-600 mb-8">Please log in to view our directory of dive professionals.</p>
            <Button onClick={() => navigate("/account")} className="bg-cyan-600 hover:bg-cyan-700">
              Log In
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white">
      <SEO title="Pros Directory | George Dives" />
      <Navigation />
      <FloatingUserMenu />
      
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Dive Professionals</h1>
            <p className="text-gray-600">
              {canViewFullProfile 
                ? "Browse and connect with certified dive professionals"
                : "View our community of certified dive professionals"
              }
            </p>
          </div>

          {/* Search and Filters */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by name, certification, or specialty..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filters
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {showFilters && (
              <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Availability</Label>
                    <select
                      value={filters.availability}
                      onChange={(e) => setFilters({...filters, availability: e.target.value})}
                      className="w-full mt-1 p-2 border rounded-lg"
                    >
                      <option value="">All</option>
                      {availabilityOptions.map(opt => (
                        <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Specialty</Label>
                    <select
                      value={filters.specialty}
                      onChange={(e) => setFilters({...filters, specialty: e.target.value})}
                      className="w-full mt-1 p-2 border rounded-lg"
                    >
                      <option value="">All</option>
                      {specialtyOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Language</Label>
                    <select
                      value={filters.language}
                      onChange={(e) => setFilters({...filters, language: e.target.value})}
                      className="w-full mt-1 p-2 border rounded-lg"
                    >
                      <option value="">All</option>
                      {languageOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Results */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading professionals...</p>
            </div>
          ) : filteredPros.length === 0 ? (
            <Card className="p-12 text-center">
              <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Professionals Found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPros.map((pro) => {
                const profile = pro.pro_profile || {};
                const certs = profile.certifications || [];
                
                return (
                  <Card key={pro.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start gap-4">
                        {pro.profile_pic ? (
                          <img
                            src={getImageUrl(pro.profile_pic)}
                            alt={pro.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-xl">
                            {pro.name?.charAt(0) || "?"}
                          </div>
                        )}
                        <div className="flex-1">
                          <CardTitle className="text-lg">{pro.name}</CardTitle>
                          {profile.availability && (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs mt-1 ${
                              profile.availability === "available" ? "bg-green-100 text-green-700" :
                              profile.availability === "looking" ? "bg-yellow-100 text-yellow-700" :
                              "bg-gray-100 text-gray-700"
                            }`}>
                              {profile.availability.charAt(0).toUpperCase() + profile.availability.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Badges */}
                      {pro.badges?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {pro.badges.map((badge) => (
                            <span
                              key={badge.id}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${badge.color || 'bg-yellow-100 text-yellow-700'}`}
                              title={badge.description}
                            >
                              {badge.icon} {badge.name}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Bio */}
                      {profile.bio && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700">{profile.bio}</p>
                        </div>
                      )}

                      {/* Certifications - Full list */}
                      {certs.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                            <Award className="w-4 h-4" />
                            <span>Certifications</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {certs.map((cert, idx) => (
                              <span key={idx} className="bg-cyan-50 text-cyan-700 px-2 py-1 rounded text-xs">
                                {cert.name} {cert.level}
                                {cert.date && <span className="text-cyan-500 ml-1">({cert.date})</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Specialties - Full list */}
                      {profile.specialties?.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                            <Briefcase className="w-4 h-4" />
                            <span>Specialties</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {profile.specialties.map((specialty, idx) => (
                              <span key={idx} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Languages */}
                      {profile.languages?.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                            <Languages className="w-4 h-4" />
                            <span>Languages</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {profile.languages.map((lang, idx) => (
                              <span key={idx} className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs">
                                {lang}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Location Preference */}
                      {profile.location_preference && (
                        <div className="mb-4">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>Prefers: {profile.location_preference}</span>
                          </div>
                        </div>
                      )}

                      {/* Experience Stats & CV - Only for dive centers/admin */}
                      {canViewFullProfile && (
                        <div className="mt-4 pt-4 border-t">
                          {/* Experience Stats */}
                          {(profile.student_count || profile.number_of_dives) && (
                            <div className="flex flex-wrap gap-4 mb-4 p-3 bg-amber-50 rounded-lg">
                              {profile.student_count && (
                                <div className="text-center">
                                  <p className="text-lg font-bold text-amber-700">{profile.student_count}</p>
                                  <p className="text-xs text-amber-600">Students Certified</p>
                                </div>
                              )}
                              {profile.number_of_dives && (
                                <div className="text-center">
                                  <p className="text-lg font-bold text-amber-700">{profile.number_of_dives}</p>
                                  <p className="text-xs text-amber-600">Dives Logged</p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* CV & Contact buttons */}
                          <div className="flex gap-2">
                            {profile.cv_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`${BACKEND_URL}${profile.cv_url}`, '_blank')}
                                className="flex-1"
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                View CV
                              </Button>
                            )}
                            {canContact && (
                              <Button
                                size="sm"
                                onClick={() => setContactModal(pro)}
                                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Contact
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Contact Modal */}
      {contactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Contact {contactModal.name}</CardTitle>
                <button onClick={() => setContactModal(null)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Your Email (for reply)</Label>
                <Input
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Introduce yourself and describe the opportunity..."
                  rows={5}
                />
              </div>
              <Button
                onClick={handleContact}
                disabled={sendingContact}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {sendingContact ? "Sending..." : "Send Message"}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Your message will be sent via George Dives. The professional can reply directly to your email.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProsDirectory;
